import Vue from "vue";
import { IFlowDeleteInput, SortOrderType } from "@/generated/types";
import { BaseFieldTypeEnum, DateFieldTypeEnum, IApiState } from "@/models";
import {
  IFlowItem,
  deleteFlow,
  updateFlow,
  watchFlowsByApiId,
} from "@/models/flow";
import { Component, Prop, Watch } from "vue-property-decorator";
import { IQueryListFlowsByApiArgs } from "@/generated/types";
import { BAlert, BContainer } from "@/lib/typed-bootstrap";
import tableActionsStyle from "@/components/shared/table/TableActions.component.module.scss";
import TableActions from "@/components/shared/table/TableActions.component";
import Table from "@/components/shared/table/Table.component";
import { TableSelectionState } from "@/components/shared/table/table-selection-state";
import {
  ICellChangePayload,
  ITableField,
} from "@/components/shared/table/types";
import { TableEditingState } from "@/components/shared/table/table-editing-state";
import { SelectedDataExtractor } from "@/components/shared/table/selected-data-extractor";
import { ListDataManager } from "@/utils/list-data-manager";
import styles from "./ProjectFlows.view.module.scss";
import ApiManagementSectionHeader from "@/components/api-management/ApiManagementSectionHeader.component";
import Plate from "@/components/shared/Plate.component";
import ApiManagementCreateFlow from "@/components/api-management/ApiManagementCreateFlow.component";
import { getIconForFieldType } from "@/components/shared/table/utils";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import _pick from "lodash/pick";
import { getErrorMessage } from "@/utils";

const getInitialVariables = (apiId: string): IQueryListFlowsByApiArgs => ({
  limit: 10,
  nextToken: null,
  filter: undefined,
  sortOrder: SortOrderType.DESC,
  apiId,
});

@Component
export default class ProjectFlows extends UserPermissions {
  @Prop({ required: true }) private value: IApiState;

  private isCreationPlateOpen = false;
  private listDataManager = Vue.observable(
    new ListDataManager<
      { items: IFlowItem[]; nextToken?: string | null },
      IQueryListFlowsByApiArgs
    >(watchFlowsByApiId, {
      perPage: 10,
      pageSizes: [10, 50],
      currentPage: 1,
    }),
  );
  private hasEditColumn = true;

  $refs!: {
    plate?: Plate;
    tableActions?: TableActions;
  };

  @Watch("value.id", { immediate: true })
  onApiIdChange() {
    if (this.value.id) {
      this.listDataManager.subscribeToList(getInitialVariables(this.value.id));
    }
  }

  private get items() {
    return this.listDataManager.data;
  }

  private get tableSelectionState() {
    return new TableSelectionState(false, false);
  }

  private get fields(): ITableField[] {
    return [
      {
        label: "Updated At",
        key: "updatedAt",
        type: DateFieldTypeEnum.DateTime,
        readOnly: true,
      },
      { label: "Name", key: "name", type: BaseFieldTypeEnum.String },
    ].map((field) => ({
      ...field,
      iconSrc: getIconForFieldType(field.type).light,
      inverseIconSrc: getIconForFieldType(field.type).dark,
    }));
  }

  private get tableEditingState() {
    return Vue.observable(new TableEditingState(this.fields));
  }

  private closeAddEntryPlate() {
    this.isCreationPlateOpen = false;
  }

  private openAddEntryPlate() {
    this.isCreationPlateOpen = true;
  }

  private async onDeleteEntries() {
    try {
      if (this.hasAdminPermissions) {
        const selectedEntries: IFlowDeleteInput[] = this.listDataManager
          .entriesFromSelectionState(this.tableSelectionState)
          .map((entry) => _pick(entry, ["id"]) as IFlowDeleteInput);

        if (
          window.confirm(
            this.$t("api_details_data_delete_entries_confirm", {
              numOfEntries:
                selectedEntries.length > 1
                  ? this.$t("global_many_entries", {
                      numOfEntries: selectedEntries.length,
                    })
                  : this.$t("global_single_entry"),
              typeName: "Flow",
            }).toString(),
          )
        ) {
          await Promise.all(
            selectedEntries.map((entry) => deleteFlow({ id: entry.id })),
          );
        }
      } else {
        throw new Error(
          this.$t("error_api_management_requires_role").toString(),
        );
      }
    } catch (err) {
      this.listDataManager.errorMessage = getErrorMessage(err);
    }
  }

  private selectedDataExtractor = new SelectedDataExtractor(
    [],
    [],
    false,
    this.hasEditColumn,
  );

  private get addEntryButtonRef() {
    return this.$refs.tableActions?.addEntryButtonRef;
  }

  private async handleCellChange(payload: ICellChangePayload) {
    await updateFlow({
      id: `${payload.item?.id ?? ""}`,
      [payload.field]: payload.value,
    });
    this.tableEditingState.isEditing = false;
    this.tableEditingState.shouldReplaceContent = false;
    this.tableSelectionState.deselectAll();
  }

  private handleEditTableEntry(index: number) {
    const flow = this.items[index];
    this.$router.push({
      name: "project-flow-editor",
      params: {
        apiId: this.value.id,
        flowId: flow.id,
      },
    });
  }

  render() {
    return (
      <BContainer class="api__view-setup" fluid>
        <ApiManagementSectionHeader
          title={this.$t("api_details_section_title_flows").toString()}
          description={this.$t(
            "api_details_section_description_flows",
          ).toString()}
        />
        <BAlert
          show={this.listDataManager.showErrorMessage}
          variant="danger"
          data-testid="apiSettingsFormError"
        >
          {this.listDataManager.errorMessage}
        </BAlert>
        <TableActions
          class={tableActionsStyle.roundedBorderTop}
          canAddItems={this.canPublishApi}
          onAddEntry={this.openAddEntryPlate}
          withDelete
          canDeleteItems={this.tableSelectionState.hasOnlyFullRowsSelected}
          onDeleteEntries={this.onDeleteEntries}
          ref="tableActions"
        />
        {this.isCreationPlateOpen && this.addEntryButtonRef && (
          <ApiManagementCreateFlow
            anchor={this.addEntryButtonRef}
            onClose={this.closeAddEntryPlate}
            apiId={this.value.id}
          />
        )}
        <div class={styles.progressPlaceholder} />
        <Table
          ref="table"
          items={this.items}
          hasMoreItems={!!this.listDataManager.listVariables?.nextToken}
          withEditColumn={this.hasEditColumn}
          withSelectionColumn
          fields={this.fields}
          selectedDataExtractor={this.selectedDataExtractor}
          tableSelectionState={this.tableSelectionState}
          tableEditingState={this.tableEditingState}
          onPaginationChange={this.listDataManager.onPaginationChange}
          paginationState={this.listDataManager.paginationState}
          onEditTableEntry={this.handleEditTableEntry}
          onCellChange={this.handleCellChange}
        >
          {this.$t("form_table_empty_text")}
        </Table>
      </BContainer>
    );
  }
}
